import { useQueryClient } from '@tanstack/react-query';

import { EmailFolderStateDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { useEmailsContext } from '@/views/emails/context/EmailsContext.ts';
import { refreshThreadList } from '@/views/emails/util/threadListCache.util.ts';
import { useCurrentThreadsQueryKey } from '@/views/emails/util/useCurrentThreadsQueryKey.util.ts';

type Return = {
  getFolders: () => EmailFolderStateDto[];
  isFetchingCurrentThreads: boolean;
  refetchCurrentThreads: (threadId?: string) => Promise<void>;
  refetchFolders: () => Promise<void>;
  setFolders: (folders: EmailFolderStateDto[]) => void;
};

export const useEmailsHandlers = (): Return => {
  const queryClient = useQueryClient();
  const { emailsState } = useEmailsContext();
  const { userId } = useUserContext();

  const currentThreadsKey = useCurrentThreadsQueryKey();

  const foldersKey = [QueryKeyEnum.EMAILS.FOLDERS_STATE, userId];

  return {
    getFolders: () => {
      return queryClient.getQueryData<EmailFolderStateDto[]>(foldersKey) ?? [];
    },
    isFetchingCurrentThreads:
      queryClient.getQueryState(currentThreadsKey)?.fetchStatus === 'fetching',
    refetchCurrentThreads: async threadId => {
      const threadIdToRefetch = threadId ?? emailsState.selectedThread;

      await refreshThreadList(queryClient);

      if (!threadIdToRefetch) {
        return;
      }

      await queryClient.refetchQueries(
        {
          queryKey: [QueryKeyEnum.EMAILS.THREAD, threadIdToRefetch],
        },
        { throwOnError: false },
      );
    },
    refetchFolders: async () => {
      await queryClient.refetchQueries({ queryKey: foldersKey });
    },
    setFolders: folders => {
      queryClient.setQueryData(foldersKey, folders);
    },
  };
};
